/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";

import { Asset, AssetAssignment, Effect, Setter, User } from "../../utils/Interfaces";

import { Box, Typography, Stack, CircularProgress, Button, Tooltip } from "@mui/material";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { listAssetsForUser, updateAvailableAssets } from "./assetsFunctions";

import { customScrollbarStyle } from "../../utils/Functions";
import { MobileFriendly } from "@mui/icons-material";

import AssetAssignmentCard from "./AssetAssignmentCard";
import { useAppDispatch } from "../../app/hooks";

export interface UserAssetsManagerProps {
    user: User;
    onOpenAssetAssignModal: Effect;
    onOpenModifyAssetModal: Setter<Asset>;
}

const UserAssetsPanel = (props: UserAssetsManagerProps) => {
    const { user, onOpenAssetAssignModal, onOpenModifyAssetModal } = props;

    const dispatch: Dispatch<AnyAction> = useAppDispatch();
    const [loading, setLoading] = useState<boolean>(false);

    const [assetAssignments, setAssetAssignments] = useState<AssetAssignment[]>([]);

    function refreshAssetsForUser(showLoading: boolean = true) {
        if (user !== null && user !== undefined) {
            listAssetsForUser(user.id, dispatch, setAssetAssignments, showLoading ? setLoading : () => {});
        }
    }

    useEffect(() => {
        refreshAssetsForUser();
    }, [user, dispatch]);

    if (user === undefined || user === null) {
        return <Typography>Please select a user to continue.</Typography>;
    }

    if (loading) {
        return (
            <Stack direction="row" justifyContent="center">
                <CircularProgress size={60} />
            </Stack>
        );
    }

    return (
        <Box>
            <Box sx={{ ml: 2 }}>
                <Stack direction="row" justifyContent="flex-start">
                    <Stack direction="column" justifyContent="column">
                        <Typography variant="h5" sx={{ mb: 2 }} textAlign="center">
                            {`Assets for ${user?.first_name} ${user?.last_name}`}
                        </Typography>
                    </Stack>
                    <Tooltip title="Assign asset to user">
                        <Button size="small" variant="contained" onClick={() => onOpenAssetAssignModal()} sx={{ mb: 1, ml: 3 }}>
                            <Stack direction="row" justifyContent="flex-start">
                                <MobileFriendly fontSize="small" />
                                <Typography>&nbsp;&nbsp;&nbsp;Assign Asset</Typography>
                            </Stack>
                        </Button>
                    </Tooltip>
                </Stack>
                <Box
                    sx={{
                        maxHeight: "calc(100vh - 200px)",
                        overflowY: "auto",
                        overflowX: "hidden",
                        ...customScrollbarStyle(),
                    }}
                >
                    {assetAssignments.map((assignment: AssetAssignment, index: number) => {
                        return (
                            <AssetAssignmentCard
                                user={user}
                                assignment={assignment}
                                key={index}
                                onAssetUnassigned={() => {
                                    refreshAssetsForUser(false);
                                    updateAvailableAssets(dispatch, () => {});
                                }}
                                onOpenModifyAssetModal={onOpenModifyAssetModal}
                            />
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

export default UserAssetsPanel;
